if (!customElements.get('feedback-form')) {
  const RATING_WORDS = ['', 'Disappointing', 'Could be better', 'Lovely', 'Wonderful', 'Exquisite'];
  const STEP_NAMES = ['', 'Rating', 'Your words', 'Image', 'Review'];
  const LAST_STEP = 4;
  const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
  const MAX_IMAGE_EDGE = 2000;
  const TOKEN_WAIT_MS = 10000;
  const ERRORS = {
    invalid_rating: 'Please choose a star rating.',
    invalid_message: 'Choose a highlight or write a few words.',
    captcha_failed: 'We could not verify this browser. Please try again.',
    invalid_image: 'That image could not be read. Please try a JPEG or PNG.',
    image_too_large: 'That image is too large. Please choose one under 5 MB.',
    too_large: 'That image is too large. Please choose one under 5 MB.',
    rate_limited: 'Too many attempts. Please wait a minute and try again.',
    default: 'Something went wrong. Please try again in a moment.',
  };
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  let turnstileScript;
  function loadTurnstile() {
    turnstileScript ??= new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.onload = () => resolve(window.turnstile);
      script.onerror = reject;
      document.head.appendChild(script);
    });
    return turnstileScript;
  }

  // Re-encodes through a canvas: applies EXIF rotation, strips all metadata and keeps uploads small.
  async function prepareImage(file) {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_IMAGE_EDGE / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
    if (!blob) throw new Error('encode_failed');
    return blob;
  }

  class FeedbackForm extends HTMLElement {
    connectedCallback() {
      this.apiUrl = this.dataset.apiUrl.replace(/\/+$/, '');
      this.form = this.querySelector('form');
      this.errorEl = this.querySelector('[data-error]');
      this.submitBtn = this.querySelector('[data-submit]');
      this.textarea = this.form.querySelector('textarea');
      this.step = 1;
      this.image = null;
      this.chips = new Set();
      this.token = '';

      const params = new URLSearchParams(window.location.search);
      this.order = { id: params.get('order') || '', name: params.get('name') || '', sig: params.get('sig') || '' };

      this.bindSheen();
      this.bindNavigation();
      this.bindStars();
      this.bindChips();
      this.bindMessage();
      this.bindImage();
      this.form.addEventListener('submit', (event) => this.onSubmit(event));

      if (this.hasAttribute('data-design-mode')) {
        this.querySelector('[data-order-label]').textContent = 'Order #1001 · Preview';
        this.renderPieces([
          { title: 'Your first piece', variant: 'Size M', quantity: 1, image: null },
          { title: 'Your second piece', variant: null, quantity: 2, image: null },
        ]);
        this.show('form');
        this.goTo(1, { focus: false });
        return;
      }
      this.init();
    }

    async init() {
      if (!this.apiUrl || !this.order.id || !this.order.sig) return this.show('invalid');
      try {
        const query = new URLSearchParams({ order: this.order.id, name: this.order.name, sig: this.order.sig });
        const res = await fetch(`${this.apiUrl}/api/status?${query}`);
        const data = await res.json();
        if (!res.ok) return this.show('invalid');
        if (data.submitted) return this.show('already');
        this.renderPieces(data.items || []);
      } catch {
        return this.show('invalid');
      }
      this.querySelector('[data-order-label]').textContent = `Order ${this.order.name}`;
      this.show('form');
      this.goTo(1, { focus: false });
    }

    // The products in this order, shown above the form so customers know what they're reviewing.
    renderPieces(items) {
      const wrap = this.querySelector('[data-pieces]');
      const list = this.querySelector('[data-pieces-list]');
      list.replaceChildren();
      if (!items.length) {
        wrap.hidden = true;
        return;
      }
      items.forEach((item, i) => {
        const li = document.createElement('li');
        li.className = 'lx-piece';
        li.style.setProperty('--i', i);

        const frame = document.createElement('div');
        frame.className = 'lx-piece-frame';
        if (item.quantity > 1) {
          const qty = document.createElement('span');
          qty.className = 'lx-piece-qty';
          qty.textContent = `× ${item.quantity}`;
          frame.appendChild(qty);
        }
        if (item.image) {
          const img = document.createElement('img');
          img.alt = item.alt || item.title;
          img.width = 300;
          img.height = 375;
          img.loading = i < 4 ? 'eager' : 'lazy';
          img.decoding = 'async';
          img.addEventListener(
            'load',
            () => {
              img.classList.add('is-loaded');
              this.updatePiecesScroll();
            },
            { once: true }
          );
          img.src = item.image;
          frame.appendChild(img);
        } else {
          const mono = document.createElement('span');
          mono.className = 'lx-piece-mono';
          mono.setAttribute('aria-hidden', 'true');
          mono.textContent = 'M';
          frame.appendChild(mono);
        }

        const title = document.createElement('p');
        title.className = 'lx-piece-title';
        title.textContent = item.title;
        li.append(frame, title);
        if (item.variant) {
          const variant = document.createElement('p');
          variant.className = 'lx-piece-variant';
          variant.textContent = item.variant;
          li.appendChild(variant);
        }
        list.appendChild(li);
      });
      list.dataset.count = String(items.length);
      wrap.hidden = false;
      this.bindPiecesCarousel();
      this.updatePiecesLayout();
    }

    // Phones show up to 2 pieces side by side and swipe through more; desktop fits 4.
    bindPiecesCarousel() {
      if (this.piecesBound) return;
      this.piecesBound = true;
      const list = this.querySelector('[data-pieces-list]');
      this.piecesQuery = window.matchMedia('(min-width: 750px)');
      this.piecesQuery.addEventListener('change', () => this.updatePiecesLayout());

      let frame = 0;
      list.addEventListener(
        'scroll',
        () => {
          cancelAnimationFrame(frame);
          frame = requestAnimationFrame(() => this.updatePiecesScroll());
          this.querySelector('[data-pieces-hint]').classList.add('is-faded');
        },
        { passive: true }
      );
      window.addEventListener('resize', () => this.updatePiecesScroll(), { passive: true });

      const step = (direction) => {
        const piece = list.querySelector('.lx-piece');
        const distance = piece ? (piece.getBoundingClientRect().width + 14) * 2 : list.clientWidth * 0.8;
        list.scrollBy({ left: direction * distance, behavior: reducedMotion() ? 'auto' : 'smooth' });
      };
      this.querySelector('[data-pieces-prev]').addEventListener('click', () => step(-1));
      this.querySelector('[data-pieces-next]').addEventListener('click', () => step(1));
    }

    updatePiecesLayout() {
      const list = this.querySelector('[data-pieces-list]');
      const count = list.children.length;
      const carousel = count > (this.piecesQuery?.matches ? 4 : 2);
      list.classList.toggle('is-carousel', carousel);
      list.classList.remove('is-scrolled', 'at-end');
      list.scrollLeft = 0;
      this.querySelector('[data-pieces-meta]').hidden = !carousel;
      const prev = this.querySelector('[data-pieces-prev]');
      const next = this.querySelector('[data-pieces-next]');
      prev.hidden = !carousel;
      next.hidden = !carousel;
      prev.disabled = true;
      next.disabled = false;
      const hint = this.querySelector('[data-pieces-hint]');
      hint.textContent = this.piecesQuery?.matches ? `${count} pieces` : `Swipe to see all ${count}`;
      hint.classList.remove('is-faded');
      requestAnimationFrame(() => this.updatePiecesScroll());
    }

    updatePiecesScroll() {
      const list = this.querySelector('[data-pieces-list]');
      if (!list.classList.contains('is-carousel')) return;
      const max = list.scrollWidth - list.clientWidth;
      const progress = max > 0 ? list.scrollLeft / max : 0;
      const visible = list.scrollWidth ? list.clientWidth / list.scrollWidth : 1;
      const thumb = this.querySelector('[data-pieces-thumb]');
      thumb.style.setProperty('--thumb-w', `${Math.max(18, visible * 100)}%`);
      thumb.style.setProperty('--thumb-x', `${progress * (100 / Math.max(visible, 0.18) - 100)}%`);
      list.classList.toggle('is-scrolled', list.scrollLeft > 4);
      list.classList.toggle('at-end', list.scrollLeft >= max - 4);
      this.querySelector('[data-pieces-prev]').disabled = list.scrollLeft <= 4;
      this.querySelector('[data-pieces-next]').disabled = list.scrollLeft >= max - 4;
    }

    show(state) {
      this.querySelectorAll('[data-state]').forEach((el) => {
        el.hidden = el.dataset.state !== state;
      });
      this.querySelectorAll('[data-state-show]').forEach((el) => {
        el.hidden = el.dataset.stateShow !== state;
      });
      const pieces = this.querySelector('[data-pieces]');
      if (state !== 'form') pieces.hidden = true;
      else if (this.querySelector('[data-pieces-list]').children.length) pieces.hidden = false;
      const heading = this.querySelector(`[data-state="${state}"] h2`);
      if (heading) heading.focus({ preventScroll: true });
    }

    // Soft light that follows the pointer across the section background.
    bindSheen() {
      const section = this.closest('section');
      if (!section || !window.matchMedia('(hover: hover)').matches || reducedMotion()) return;
      let frame = 0;
      section.addEventListener('pointermove', (event) => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const rect = section.getBoundingClientRect();
          section.style.setProperty('--mx', `${((event.clientX - rect.left) / rect.width) * 100}%`);
          section.style.setProperty('--my', `${((event.clientY - rect.top) / rect.height) * 100}%`);
        });
      });
    }

    /* ---------- Steps ---------- */

    bindNavigation() {
      this.querySelectorAll('[data-next]').forEach((btn) => btn.addEventListener('click', () => this.next()));
      this.querySelectorAll('[data-back]').forEach((btn) => btn.addEventListener('click', () => this.goTo(this.step - 1)));
      this.querySelectorAll('[data-goto]').forEach((btn) =>
        btn.addEventListener('click', () => this.goTo(Number(btn.dataset.goto)))
      );
    }

    next() {
      const error = this.validate(this.step);
      if (error) return this.setError(error);
      this.goTo(this.step + 1);
    }

    validate(step) {
      if (step >= 1 && !this.rating) return ERRORS.invalid_rating;
      if (step >= 2 && !this.composeMessage()) return ERRORS.invalid_message;
      return '';
    }

    goTo(step, { focus = true } = {}) {
      const target = Math.min(Math.max(step, 1), LAST_STEP);
      const backwards = target < this.step;
      this.step = target;
      this.setError('');

      this.querySelectorAll('[data-step]').forEach((panel) => {
        const active = Number(panel.dataset.step) === target;
        panel.classList.toggle('is-active', active);
        panel.classList.toggle('is-back', active && backwards);
      });
      this.querySelectorAll('[data-progress] li').forEach((li, i) => {
        li.classList.toggle('is-done', i + 1 < target);
        li.classList.toggle('is-current', i + 1 === target);
      });
      this.querySelector('[data-progress]').style.setProperty('--lx-progress', (target - 1) / (LAST_STEP - 1));
      this.querySelector('[data-step-announce]').textContent = `Step ${target} of ${LAST_STEP}: ${STEP_NAMES[target]}`;

      if (target === 2) this.updatePrompt();
      if (target === LAST_STEP) {
        this.renderReview();
        this.renderTurnstile();
      }
      if (focus) {
        this.querySelector(`[data-step="${target}"] .lx-q`).focus({ preventScroll: true });
        const top = this.getBoundingClientRect().top;
        if (top < 0) this.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
      }
    }

    /* ---------- Step 1: rating ---------- */

    get rating() {
      return Number(this.form.querySelector('input[name="rating"]:checked')?.value || 0);
    }

    bindStars() {
      const labels = [...this.querySelectorAll('[data-stars] label')];
      const word = this.querySelector('[data-rating-word]');
      const paint = (value) => labels.forEach((label, i) => label.classList.toggle('is-on', i < value));
      const setWord = (value) => {
        const text = RATING_WORDS[value];
        if (word.textContent === text) return;
        word.textContent = text;
        word.classList.remove('is-changing');
        void word.offsetWidth;
        word.classList.add('is-changing');
      };

      labels.forEach((label, i) => {
        label.addEventListener('mouseenter', () => {
          paint(i + 1);
          setWord(i + 1);
        });
        label.addEventListener('mouseleave', () => {
          paint(this.rating);
          setWord(this.rating);
        });
      });
      this.form.querySelectorAll('input[name="rating"]').forEach((input, i) => {
        input.addEventListener('change', () => {
          paint(this.rating);
          setWord(this.rating);
          this.setError('');
          this.sparkle(labels[i]);
        });
      });
    }

    sparkle(label) {
      if (reducedMotion()) return;
      for (let i = 0; i < 8; i++) {
        const spark = document.createElement('span');
        spark.className = 'lx-spark';
        spark.style.setProperty('--a', `${i * 45 + 22}deg`);
        label.appendChild(spark);
        spark.addEventListener('animationend', () => spark.remove());
      }
    }

    /* ---------- Step 2: words ---------- */

    updatePrompt() {
      const positive = this.rating >= 4;
      this.querySelector('[data-prompt]').textContent = positive
        ? this.dataset.positivePrompt
        : this.dataset.negativePrompt;
    }

    bindChips() {
      this.querySelectorAll('[data-chip]').forEach((chip) => {
        chip.addEventListener('click', () => {
          const name = chip.dataset.chip;
          const pressed = !this.chips.has(name);
          if (pressed) this.chips.add(name);
          else this.chips.delete(name);
          chip.setAttribute('aria-pressed', String(pressed));
          this.setError('');
        });
      });
    }

    bindMessage() {
      const count = this.querySelector('[data-count]');
      this.textarea.addEventListener('input', () => {
        count.textContent = `${this.textarea.value.length} / 1900`;
        this.setError('');
      });
    }

    composeMessage() {
      const parts = [];
      if (this.chips.size) parts.push(`${this.rating >= 4 ? 'Loved' : 'Could be better'}: ${[...this.chips].join(', ')}`);
      const text = this.textarea.value.trim();
      if (text) parts.push(text);
      return parts.join('\n\n');
    }

    /* ---------- Step 3: image ---------- */

    bindImage() {
      const input = this.querySelector('[data-image-input]');
      const drop = this.querySelector('[data-drop]');
      const photo = this.querySelector('[data-photo]');
      const thumb = this.querySelector('[data-image-thumb]');
      const nextLabel = this.querySelector('[data-photo-next]');

      const clear = () => {
        if (thumb.src) URL.revokeObjectURL(thumb.src);
        thumb.removeAttribute('src');
        this.image = null;
        input.value = '';
        photo.hidden = true;
        drop.hidden = false;
        nextLabel.textContent = 'Skip';
      };

      const accept = async (file) => {
        if (!file) return;
        this.setError('');
        try {
          const blob = await prepareImage(file);
          if (blob.size > MAX_IMAGE_BYTES) throw new Error('too_large');
          clear();
          this.image = blob;
          thumb.src = URL.createObjectURL(blob);
          drop.hidden = true;
          photo.hidden = false;
          nextLabel.textContent = 'Continue';
        } catch (err) {
          clear();
          this.setError(err.message === 'too_large' ? ERRORS.image_too_large : ERRORS.invalid_image);
        }
      };

      input.addEventListener('change', () => accept(input.files[0]));
      ['dragenter', 'dragover'].forEach((type) =>
        drop.addEventListener(type, (event) => {
          event.preventDefault();
          drop.classList.add('is-over');
        })
      );
      ['dragleave', 'drop'].forEach((type) =>
        drop.addEventListener(type, (event) => {
          event.preventDefault();
          drop.classList.remove('is-over');
        })
      );
      drop.addEventListener('drop', (event) => accept(event.dataTransfer.files[0]));
      this.querySelector('[data-image-replace]').addEventListener('click', () => input.click());
      this.querySelector('[data-image-remove]').addEventListener('click', () => {
        clear();
        input.focus();
      });
    }

    /* ---------- Step 4: review & send ---------- */

    renderReview() {
      const rating = this.rating;
      this.querySelector('[data-review-stars]').textContent = '★'.repeat(rating) + '☆'.repeat(5 - rating);
      this.querySelector('[data-review-word]').textContent = RATING_WORDS[rating];
      this.querySelector('[data-review-message]').textContent = this.composeMessage();

      const imageCell = this.querySelector('[data-review-image]');
      imageCell.replaceChildren();
      if (this.image) {
        const img = document.createElement('img');
        img.src = this.querySelector('[data-image-thumb]').src;
        img.alt = 'Your selected photo';
        img.width = 56;
        img.height = 56;
        imageCell.appendChild(img);
      } else {
        imageCell.textContent = 'None added';
      }
    }

    async renderTurnstile() {
      if (this.widgetId !== undefined || !this.dataset.siteKey || this.hasAttribute('data-design-mode')) return;
      this.widgetId = null;
      try {
        const turnstile = await loadTurnstile();
        this.widgetId = turnstile.render(this.querySelector('[data-turnstile]'), {
          sitekey: this.dataset.siteKey,
          theme: 'light',
          appearance: 'interaction-only',
          'response-field': false,
          callback: (token) => {
            this.token = token;
          },
          'expired-callback': () => {
            this.token = '';
          },
          'error-callback': () => {
            this.token = '';
          },
        });
      } catch {
        this.widgetId = undefined;
        this.setError(ERRORS.captcha_failed);
      }
    }

    // The widget verifies quietly in the background; wait briefly for it if the customer is quick.
    async waitForToken() {
      const start = Date.now();
      while (!this.token && Date.now() - start < TOKEN_WAIT_MS) {
        await new Promise((resolve) => setTimeout(resolve, 200));
      }
      return this.token;
    }

    resetTurnstile() {
      this.token = '';
      if (this.widgetId) window.turnstile.reset(this.widgetId);
    }

    setError(message) {
      this.errorEl.textContent = message;
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.step !== LAST_STEP) return this.next();
      if (this.hasAttribute('data-design-mode')) return;

      const error = this.validate(LAST_STEP);
      if (error) return this.setError(error);

      this.setError('');
      this.submitBtn.disabled = true;
      try {
        const token = await this.waitForToken();
        if (!token) return this.setError(ERRORS.captcha_failed);

        const body = new FormData();
        body.append('order_id', this.order.id);
        body.append('order_name', this.order.name);
        body.append('sig', this.order.sig);
        body.append('rating', String(this.rating));
        body.append('message', this.composeMessage());
        body.append('cf-turnstile-response', token);
        if (this.image) body.append('image', this.image, 'photo.jpg');

        const res = await fetch(`${this.apiUrl}/api/feedback`, { method: 'POST', body });
        const data = await res.json().catch(() => ({}));
        if (res.ok) return this.celebrate();
        if (data.error === 'already_submitted') return this.show('already');
        if (data.error === 'invalid_link') return this.show('invalid');
        this.setError(ERRORS[data.error] || ERRORS.default);
        this.resetTurnstile();
      } catch {
        this.setError(ERRORS.default);
        this.resetTurnstile();
      } finally {
        this.submitBtn.disabled = false;
      }
    }

    celebrate() {
      this.querySelector('[data-success-rating]').textContent = '★'.repeat(this.rating);
      this.show('success');
      if (reducedMotion()) return;
      const card = this.querySelector('.lx-card');
      for (let i = 0; i < 18; i++) {
        const dust = document.createElement('span');
        dust.className = 'lx-dust';
        dust.style.left = `${10 + Math.random() * 80}%`;
        dust.style.setProperty('--dx', `${(Math.random() - 0.5) * 80}px`);
        dust.style.setProperty('--d', `${2.4 + Math.random() * 1.6}s`);
        dust.style.setProperty('--delay', `${0.4 + Math.random() * 0.8}s`);
        card.appendChild(dust);
        dust.addEventListener('animationend', () => dust.remove());
      }
    }
  }

  customElements.define('feedback-form', FeedbackForm);
}
